import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  assertNever,
  EnrichmentRunState,
  EnrichmentStageStatus,
  MatchDisposition,
  MatchStage,
  ProspectListEnrichmentState,
  ProspectListStatus,
  type EnrichmentBatchProgress,
  type EnrichmentFeedItem,
  type EnrichmentRunProgress,
  type EnrichmentRunRead,
  type EnrichmentRunSummary,
  type ProspectListRead,
} from '@signalvote/shared';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { buildField, BATCH_SIZE, type Field } from '@/lib/fundraising/enrichmentField';
import { isUploadsRun, nobodyNew, uploadInProgress } from '@/lib/fundraising/enrichmentWork';
import { cn } from '@/lib/utils';
import { EnrichmentFieldCanvas, type FieldMode } from './EnrichmentFieldCanvas';
import { ProspectListRetry } from './ProspectListRetry';
import { ProspectListUndo } from './ProspectListUndo';

const n = (value: number) => value.toLocaleString();
const clock = (seconds: number) => {
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Seconds the run has been going. While it runs, it counts on this browser's clock from one anchor taken
 * when the run is first seen — never earlier than now, so a server clock running ahead can't pin it at
 * 0:00, and a later poll bringing a different `startedAt` (the server's, after the optimistic start)
 * can't make it jump. Once the run ends it shows the server's own start-to-finish.
 */
function useElapsedSeconds(run: EnrichmentRunRead) {
  const running = run.state === EnrichmentRunState.Running;
  const [now, setNow] = useState(() => Date.now());
  const anchor = useRef<number | null>(null);
  if (!running) anchor.current = null;
  else if (anchor.current === null) {
    anchor.current = run.startedAt ? Math.min(Date.now(), Date.parse(run.startedAt)) : Date.now();
  }

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);

  if (running) return Math.max(0, (now - anchor.current!) / 1000);
  if (!run.startedAt || !run.finishedAt) return 0;
  return Math.max(0, (Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000);
}

/** What the overlay is showing, worked out once from the run and (when adding people) the upload. */
type Phase =
  | { kind: 'starting' }
  | { kind: 'adding'; upload: ProspectListRead }
  | { kind: 'stopped'; upload: ProspectListRead }
  | { kind: 'undone' }
  | { kind: 'noNew'; upload: ProspectListRead }
  | { kind: 'alongside'; upload: ProspectListRead }
  | { kind: 'matching'; progress: EnrichmentRunProgress }
  | { kind: 'broke'; progress: EnrichmentRunProgress | null }
  /** Nobody could be matched: every batch failed, or the upload's matching did. */
  | { kind: 'unmatched'; added: boolean }
  | { kind: 'done'; summary: EnrichmentRunSummary; progress: EnrichmentRunProgress | null };

function finished(summary: EnrichmentRunSummary, progress: EnrichmentRunProgress | null): Phase {
  return summary.peopleEnriched === 0 && summary.peopleFailed > 0
    ? { kind: 'unmatched', added: false }
    : { kind: 'done', summary, progress };
}

function runPhase(run: EnrichmentRunRead): Phase {
  const progress = run.progress ?? null;
  switch (run.state) {
    case EnrichmentRunState.NeverRun:
      return { kind: 'starting' };
    case EnrichmentRunState.Running:
      return progress ? { kind: 'matching', progress } : { kind: 'starting' };
    case EnrichmentRunState.Completed:
      return run.summary ? finished(run.summary, progress) : { kind: 'starting' };
    case EnrichmentRunState.Failed:
      return { kind: 'broke', progress };
    default:
      return assertNever(run.state);
  }
}

function uploadPhase(run: EnrichmentRunRead, upload: ProspectListRead | null): Phase {
  if (!upload) return { kind: 'starting' };
  if (nobodyNew(upload)) return { kind: 'noNew', upload };
  switch (upload.status) {
    case ProspectListStatus.Pending:
    case ProspectListStatus.Importing:
      return { kind: 'adding', upload };
    case ProspectListStatus.Failed:
      return { kind: 'stopped', upload };
    case ProspectListStatus.Undone:
      return { kind: 'undone' };
    case ProspectListStatus.Imported:
      break;
    default:
      return assertNever(upload.status);
  }

  const ours = isUploadsRun(upload);
  const enrichment = upload.enrichment;
  if (enrichment?.state === ProspectListEnrichmentState.Failed) return { kind: 'unmatched', added: true };
  if (enrichment?.state === ProspectListEnrichmentState.Completed && enrichment.summary) {
    return finished(enrichment.summary, ours ? (run.progress ?? null) : null);
  }
  if (!ours) return { kind: 'alongside', upload };
  return run.state === EnrichmentRunState.Running && run.progress ? { kind: 'matching', progress: run.progress } : { kind: 'starting' };
}

/** The finished result as batches, for a result with no batches of its own to show (another run's). */
function summaryBatches(summary: EnrichmentRunSummary): EnrichmentBatchProgress[] {
  const total = summary.peopleEnriched + summary.peopleFailed;
  const share = (count: number, people: number) => Math.round((count * people) / Math.max(1, total));
  return Array.from({ length: Math.ceil(total / BATCH_SIZE) }, (_, i) => {
    const people = Math.min(BATCH_SIZE, total - i * BATCH_SIZE);
    return {
      people,
      peopleWithAnyMatch: share(summary.peopleWithAnyMatch, people),
      peopleNeedingReview: share(summary.peopleNeedingReview, people),
      peopleFailed: share(summary.peopleFailed, people),
    };
  });
}

const sum = (batches: EnrichmentBatchProgress[], key: keyof EnrichmentBatchProgress) =>
  batches.reduce((total, b) => total + b[key], 0);

const STEP_TITLE: Record<MatchStage, string> = {
  [MatchStage.Searching]: 'Searching federal records',
  [MatchStage.ExactMatch]: 'Checking exact matches',
  [MatchStage.Recovering]: 'Finding people who moved',
  [MatchStage.Judging]: 'AI judge is deciding unclear pairs',
};

const activeStep = (progress: EnrichmentRunProgress) =>
  progress.stages.find((s) => s.status === EnrichmentStageStatus.Active)?.stage ?? null;

/** The judge's verdict in the book's own words. The band only — a score is never shown. */
function verdictLabel(verdict: MatchDisposition | null | undefined): { text: string; className: string } {
  switch (verdict) {
    case MatchDisposition.AutoAccept:
      return { text: 'Match', className: 'text-emerald-700' };
    case MatchDisposition.Review:
      return { text: 'Possible match', className: 'text-amber-700' };
    case MatchDisposition.Suppress:
      return { text: 'Not them', className: 'text-muted-foreground' };
    case null:
    case undefined:
      return { text: "Couldn't check", className: 'text-red-700' };
    default:
      return assertNever(verdict);
  }
}

const FEED_SHOWN = 3;
const FEED_FADE = [1, 0.7, 0.45];

/**
 * What the matcher is doing now, and the AI judge's latest answers under it, newest on top and fading
 * down. Each answer slides in once, keyed by its place in the run.
 */
function NowPanel({ title, detail, feed, live, emptyText }: {
  title: string;
  detail?: string;
  feed: EnrichmentFeedItem[];
  /** The judge is answering now; otherwise its last answers sit back. */
  live: boolean;
  emptyText: string;
}) {
  const shown = feed.slice(-FEED_SHOWN).reverse();
  return (
    <div className="flex flex-col rounded-xl bg-muted px-4 pb-1.5 pt-3">
      <div className="flex h-6 items-center justify-between gap-3">
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          {title}
        </span>
        {detail && <span className="text-xs tabular-nums text-muted-foreground">{detail}</span>}
      </div>
      <div className={cn('mt-1 transition-opacity duration-300', live ? 'opacity-100' : 'opacity-50')}>
        {shown.length > 0 ? (
          <ul aria-label="AI judge's answers">
            {shown.map((item, i) => {
              const verdict = verdictLabel(item.verdict);
              return (
                <li
                  key={item.sequence}
                  className="flex h-[34px] items-center justify-between gap-3 border-t motion-safe:animate-ledger-expand"
                  style={{ opacity: FEED_FADE[i] }}
                >
                  <span className="flex min-w-0 items-baseline gap-1.5 overflow-hidden whitespace-nowrap">
                    <span className="text-foreground">{item.person}</span>
                    {item.place && <span className="text-xs text-muted-foreground">{item.place}</span>}
                  </span>
                  <span className={cn('whitespace-nowrap text-xs font-semibold', verdict.className)}>{verdict.text}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="grid h-[102px] place-items-center border-t text-muted-foreground">{emptyText}</div>
        )}
      </div>
    </div>
  );
}

function Swatch({ className }: { className: string }) {
  return <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-[2px]', className)} />;
}

function Legend({ peoplePerRow }: { peoplePerRow: number }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
      <div className="flex flex-wrap gap-3.5">
        <span className="flex items-center gap-1.5"><Swatch className="bg-success" />Federal giving</span>
        <span className="flex items-center gap-1.5"><Swatch className="bg-warning" />To confirm</span>
        <span className="flex items-center gap-1.5"><Swatch className="bg-primary/50" />Being checked</span>
        <span className="flex items-center gap-1.5"><Swatch className="bg-border" />No record</span>
      </div>
      <span className="whitespace-nowrap">Each row is {n(peoplePerRow)} people</span>
    </div>
  );
}

/** The legend, turned into the final counts. */
function ResultCounts({ summary }: { summary: EnrichmentRunSummary }) {
  const checked = summary.peopleEnriched + summary.peopleFailed;
  const none = Math.max(0, checked - summary.peopleWithAnyMatch - summary.peopleNeedingReview - summary.peopleFailed);
  const counts: [number, string, string][] = [
    [summary.peopleWithAnyMatch, 'federal giving', 'bg-success'],
    [summary.peopleNeedingReview, 'to confirm', 'bg-warning'],
    [summary.peopleFailed, "couldn't match yet", 'bg-error'],
    [none, 'no federal record', 'bg-border'],
  ];
  return (
    <ul aria-label="Result" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {counts.map(([count, label, swatch]) => (
        <li key={label} className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5 font-semibold tabular-nums text-foreground">
            <Swatch className={swatch} />
            {n(count)}
          </span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </li>
      ))}
    </ul>
  );
}

function Hero({ value, label, sub, found }: { value: string; label: string; sub?: string; found?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2.5">
        <span className={cn('text-4xl font-bold leading-none tabular-nums', found ? 'text-success' : 'text-foreground')}>
          {value}
        </span>
        <span className="font-semibold text-foreground">{label}</span>
      </div>
      {sub && <div className="text-xs tabular-nums text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Notice({ title, children, actions }: { title: string; children?: ReactNode; actions: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="font-semibold text-error">{title}</div>
        {children && <div className="text-pretty text-foreground">{children}</div>}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

/**
 * The enrichment overlay: the list as a field of people, a row per batch of 500, that resolves as the
 * matcher works down it, under the one number that's growing — people with federal giving so far. A
 * short panel says what the matcher is doing now, with the AI judge's latest answers. At the end the field
 * sorts itself and the legend turns into the final counts. With `following` set it's adding people from
 * a file first. Presentational; the run and upload come from the container.
 */
export function EnrichmentPipeline({
  run,
  following,
  upload = null,
  onClose,
  onReviewPossible,
  onSeeEveryone,
  onRetryUnmatched,
}: {
  run: EnrichmentRunRead;
  /** Adding people from a file: follow the upload, whose progress is `upload` once it has arrived. */
  following?: boolean;
  upload?: ProspectListRead | null;
  onClose: () => void;
  onReviewPossible: () => void;
  onSeeEveryone: () => void;
  /** Match again the people who couldn't be matched. */
  onRetryUnmatched: () => void;
}) {
  const elapsed = useElapsedSeconds(run);
  const phase = following ? uploadPhase(run, upload) : runPhase(run);
  // The run's clock is this overlay's only while it follows that run.
  const timed = !following || (!!upload && isUploadsRun(upload));
  const time = timed ? clock(elapsed) : null;

  const working =
    phase.kind === 'starting' ||
    phase.kind === 'adding' ||
    phase.kind === 'matching' ||
    phase.kind === 'alongside' ||
    (phase.kind === 'noNew' && uploadInProgress(phase.upload));

  const progress = phase.kind === 'matching' || phase.kind === 'broke' || phase.kind === 'done' ? phase.progress : null;
  const batches = progress?.batches ?? [];
  const fieldTotal =
    phase.kind === 'adding' || phase.kind === 'stopped' || phase.kind === 'alongside'
      ? phase.upload.rowCount
      : phase.kind === 'done' && !progress
        ? phase.summary.peopleEnriched + phase.summary.peopleFailed
        : (progress?.peopleTotal ?? 0);
  const fieldBatches = phase.kind === 'done' && !progress ? summaryBatches(phase.summary) : batches;
  const field: Field = useMemo(
    () => buildField({ total: fieldTotal, batches: fieldBatches, running: phase.kind === 'matching' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the batches only grow; their count is their identity
    [fieldTotal, fieldBatches.length, phase.kind],
  );

  const fileLine = !upload
    ? progress
      ? `${n(progress.peopleTotal)} people in your book`
      : null
    : upload.status === ProspectListStatus.Imported
      ? `${upload.originalFileName} · ${n(upload.createdCount)} new · ${n(upload.adoptedCount)} already in your book`
      : `${upload.originalFileName} · ${n(upload.rowCount)} rows`;

  let mode: FieldMode | null = null;
  let fieldLabel = '';
  let hero: ReactNode = null;
  let below: ReactNode = null;

  switch (phase.kind) {
    case 'starting':
      below = (
        <NowPanel
          title={following ? 'Adding people to your book' : 'Getting your people ready'}
          feed={[]}
          live={false}
          emptyText={following ? 'Matching starts once everyone is added.' : "The AI judge's answers appear here."}
        />
      );
      break;
    case 'adding':
    case 'stopped': {
      const { upload: u } = phase;
      const stopped = phase.kind === 'stopped';
      mode = { kind: 'adding', added: (u.rowsWatermark / Math.max(1, u.rowCount)) * field.rows.reduce((s, r) => s + r.cells.length, 0), stopped };
      fieldLabel = `${n(u.rowCount)} rows in the file; ${n(u.rowsWatermark)} added`;
      hero = stopped ? (
        <Hero value={n(u.createdCount)} label={`of ${n(u.rowCount)} people added`} sub="Stopped partway" />
      ) : (
        <Hero
          value={n(u.rowsWatermark)}
          label="people added to your book"
          sub={`of ${n(u.rowCount)} rows · matching starts once everyone's in`}
        />
      );
      below = stopped ? (
        <Notice
          title="Adding stopped partway"
          actions={
            <>
              <ProspectListRetry list={u} />
              <ProspectListUndo list={u} />
            </>
          }
        >
          It stopped at row {n(u.rowsWatermark)}. Those people are in your book; the other{' '}
          {n(Math.max(0, u.rowCount - u.rowsWatermark))} aren&apos;t yet. Matching hasn&apos;t started.
        </Notice>
      ) : (
        <NowPanel title="Adding people to your book" feed={[]} live={false} emptyText="Matching starts once everyone is added." />
      );
      break;
    }
    case 'alongside':
      mode = { kind: 'adding', added: Infinity, stopped: false };
      fieldLabel = `${n(phase.upload.rowCount)} rows in the file, waiting to be matched`;
      hero = <Hero value={n(phase.upload.createdCount)} label="people added to your book" sub="Waiting their turn to be matched" />;
      below = (
        <div className="text-pretty rounded-lg bg-muted px-3 py-2.5 text-foreground">
          Your people are being matched alongside another run that was already going. The result will show here.
        </div>
      );
      break;
    case 'matching':
    case 'broke': {
      const p = phase.progress;
      const step = p ? activeStep(p) : null;
      const doneRows = field.rows.filter((r) => r.state === 'done').length;
      mode = { kind: 'matching', step: phase.kind === 'matching' ? step : null };
      fieldLabel = `${n(fieldTotal)} people in ${field.rows.length} ${field.rows.length === 1 ? 'row' : 'rows'} of ${n(field.peoplePerRow)}; ${doneRows} ${doneRows === 1 ? 'row' : 'rows'} checked`;
      hero = (
        <Hero
          value={n(sum(batches, 'peopleWithAnyMatch'))}
          label="with federal giving so far"
          sub={`${n(p?.peopleProcessed ?? 0)} of ${n(p?.peopleTotal ?? 0)} checked · ${n(sum(batches, 'peopleNeedingReview'))} to confirm`}
        />
      );
      below =
        phase.kind === 'matching' ? (
          <NowPanel
            title={step ? STEP_TITLE[step] : `Moving to the next ${n(BATCH_SIZE)}`}
            detail={step === MatchStage.Judging ? `${n(p!.stages.find((s) => s.stage === MatchStage.Judging)?.count ?? 0)} decided` : undefined}
            feed={p!.feed}
            live={step === MatchStage.Judging}
            emptyText="The AI judge's answers appear here."
          />
        ) : (
          <Notice
            title={`Matching stopped at ${n(p?.peopleProcessed ?? 0)} of ${n(p?.peopleTotal ?? 0)}`}
            actions={
              <>
                <Button onClick={onRetryUnmatched}>Retry</Button>
                <Button variant="ghost" onClick={onSeeEveryone}>See results so far</Button>
              </>
            }
          >
            Nothing is lost. Everyone already checked keeps their result, and retrying picks up where it stopped.
          </Notice>
        );
      break;
    }
    case 'done': {
      const { summary } = phase;
      const checked = summary.peopleEnriched + summary.peopleFailed;
      const toConfirm = summary.peopleNeedingReview;
      mode = { kind: 'done' };
      fieldLabel = `${n(checked)} people, sorted by result`;
      hero = (
        <Hero
          found
          value={n(summary.peopleWithAnyMatch)}
          label="people with federal giving"
          sub={`${n(checked)} people checked${time ? ` in ${time}` : ''}`}
        />
      );
      below = (
        <>
          {summary.peopleFailed > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-error/5 py-1 pl-3.5 pr-1">
              <span className="text-pretty">{n(summary.peopleFailed)} couldn&apos;t be matched right now.</span>
              <Button variant="ghost" onClick={onRetryUnmatched}>Retry {n(summary.peopleFailed)}</Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {toConfirm > 0 && <Button onClick={onReviewPossible}>Review {n(toConfirm)} to confirm</Button>}
            <Button variant={toConfirm > 0 ? 'ghost' : 'default'} onClick={onSeeEveryone}>See everyone</Button>
          </div>
        </>
      );
      break;
    }
    case 'unmatched':
      below = (
        <Notice
          title={phase.added ? "Your people were added, but couldn't be matched right now" : 'Nobody could be matched right now'}
          actions={<Button onClick={onRetryUnmatched}>Retry</Button>}
        >
          Nothing is lost. Everyone keeps what they had, and you can try again now or later from this page.
        </Notice>
      );
      break;
    case 'noNew':
      below = (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="text-xl font-bold text-foreground">No new people</div>
            <div className="max-w-[480px] text-pretty text-muted-foreground">
              All {n(phase.upload.adoptedCount)} people in this file are already in your book, so there&apos;s nobody new to
              match.
            </div>
          </div>
          <div>
            <Button onClick={onSeeEveryone}>See them in your book</Button>
          </div>
        </div>
      );
      break;
    case 'undone':
      below = <div className="text-muted-foreground">This upload was undone.</div>;
      break;
    default:
      return assertNever(phase);
  }

  const status = working
    ? `Running${time && phase.kind !== 'adding' ? ` · ${time}` : ''} · keeps going if you close this`
    : phase.kind === 'done'
      ? time
        ? `Done in ${time}`
        : 'Done'
      : phase.kind === 'noNew'
        ? 'Nothing to match'
        : phase.kind === 'undone'
          ? 'Undone'
          : `Stopped${time ? ` · ${time}` : ''}`;

  return (
    <div className="overflow-hidden rounded-2xl border bg-card text-sm text-foreground shadow-[0_18px_40px_-12px_rgb(17_39_71/0.22)]">
      <div className="flex flex-col gap-1 pb-0 pl-6 pr-12 pt-[22px]">
        <h2 className="text-base font-semibold">Checking federal giving</h2>
        {fileLine && <div className="text-xs text-muted-foreground">{fileLine}</div>}
      </div>

      <div className="flex flex-col gap-[18px] px-6 pb-6 pt-5">
        {mode && (
          <div className="flex flex-col gap-2.5">
            {hero}
            <EnrichmentFieldCanvas field={field} mode={mode} label={fieldLabel} />
            {phase.kind === 'done' ? <ResultCounts summary={phase.summary} /> : <Legend peoplePerRow={field.peoplePerRow} />}
          </div>
        )}
        {below}
      </div>

      <div className="flex min-h-[65px] items-center justify-between gap-3 border-t py-2.5 pl-6 pr-3">
        <span role="status" className="text-xs tabular-nums text-muted-foreground">
          {status}
        </span>
        {working ? (
          <Button variant="ghost" onClick={onClose}>
            Keep it running in the background
          </Button>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
}
